import { BREADCRUMB_ITEMS_TYPES } from '../../../../util/enums/invoice-management';

/**
 * Utility functions for building breadcrumb paths in invoice management
 */

/**
 * Finds a stage recursively in the project hierarchy
 * @param {Array} stages - Array of stages to search in
 * @param {string} stageId - ID of the stage to find
 * @returns {Object|null} - Found stage object or null
 */
export const findStageRecursively = (stages, stageId) => {
    for (const stage of stages) {
        if (stage.id === stageId) {
            return stage;
        }
        if (stage.stages && stage.stages.length > 0) {
            const found = findStageRecursively(stage.stages, stageId);
            if (found) return found;
        }
    }
    return null;
};

/**
 * Flattens all stages in a project for easier lookup
 * @param {Array} stages - Array of stages to flatten
 * @returns {Array} - Flattened array of all stages
 */
export const flattenStages = (stages) => {
    const allStages = [];
    const flatten = (stageArray) => {
        stageArray.forEach(s => {
            allStages.push(s);
            if (s.stages && s.stages.length > 0) {
                flatten(s.stages);
            }
        });
    };
    flatten(stages);
    return allStages;
};

/**
 * Builds complete breadcrumb path for nested stages
 * @param {Object} project - Project object
 * @param {Object} stage - Stage object
 * @returns {Array} - Array of breadcrumb items
 */
export const buildStageBreadcrumbPath = (project, stage) => {
    const breadcrumbItems = [
        {
            label: project.name,
            value: project.id,
            type: BREADCRUMB_ITEMS_TYPES.PROJECT,
            id: project.id
        }
    ];

    // If stage has hierarchy information, use it to build the complete path
    if (stage.hierarchy && Array.isArray(stage.hierarchy)) {
        // Create a flat list of all stages in the project for easier lookup
        const allStages = flattenStages(project.stages);

        // Build breadcrumb items for each stage ID in the hierarchy
        stage.hierarchy.forEach((stageId, index) => {
            // Find the stage object that matches this ID
            const stageObj = allStages.find(s => s.id === stageId);
            if (stageObj) {
                const isLastStage = index === stage.hierarchy.length - 1;
                breadcrumbItems.push({
                    label: stageObj.name,
                    value: stageObj.id,
                    type: BREADCRUMB_ITEMS_TYPES.STAGE,
                    isActive: isLastStage,
                    id: stageObj.id,
                    projectId: project.id, // projectId for hover functionality
                });
            }
        });
    } else {
        // Fallback: build hierarchy by traversing the stage tree
        const findPathToStage = (targetStageId, stages, currentPath = []) => {
            for (const s of stages) {
                const newPath = [...currentPath, s];
                if (s.id === targetStageId) {
                    return newPath;
                }
                if (s.stages && s.stages.length > 0) {
                    const result = findPathToStage(targetStageId, s.stages, newPath);
                    if (result) return result;
                }
            }
            return null;
        };

        const stagePath = findPathToStage(stage.id, project.stages);
        if (stagePath && stagePath.length > 0) {
            stagePath.forEach((stageObj, index) => {
                const isLastStage = index === stagePath.length - 1;
                breadcrumbItems.push({
                    label: stageObj.name,
                    value: stageObj.id,
                    type: BREADCRUMB_ITEMS_TYPES.STAGE,
                    isActive: isLastStage,
                    id: stageObj.id,
                    projectId: project.id, // projectId for hover functionality
                });
            });
        } else {
            // Final fallback: just add the current stage
            breadcrumbItems.push({
                label: stage.name,
                value: stage.id,
                type: BREADCRUMB_ITEMS_TYPES.STAGE,
                isActive: true,
                id: stage.id,
                projectId: project.id, // projectId for hover functionality
            });
        }
    }

    return breadcrumbItems;
};

/**
 * Adds onClick handlers to breadcrumb items
 * @param {Array} breadcrumbItems - Array of breadcrumb items
 * @param {Object} project - Project object
 * @param {Function} handleProjectSelect - Function to handle project selection
 * @param {Function} handleStageSelect - Function to handle stage selection
 * @returns {Array} - Array of breadcrumb items with onClick handlers
 */
export const addBreadcrumbHandlers = (breadcrumbItems, project, handleProjectSelect, handleStageSelect) => {
    return breadcrumbItems.map((item) => {
        if (item.type === BREADCRUMB_ITEMS_TYPES.PROJECT) {
            return { ...item, onClick: () => handleProjectSelect(project) };
        } else if (item.type === BREADCRUMB_ITEMS_TYPES.STAGE && !item.isActive) {
            // Find the stage object for this breadcrumb item
            const stageObj = findStageRecursively(project.stages, item.id);
            return stageObj ? { ...item, onClick: () => handleStageSelect(project, stageObj) } : item;
        }
        return item;
    });
}; 